/**
 * Holly's clip set: breathing, trapped, walking, the reach, and the doze.
 *
 * ## What she has that Rosalie does not, and what she does not have
 *
 * A neck, a head on a separate link, and the ability to get her front feet off
 * the ground. No ears to flick, no snout to pitch, no tail pivot, no roll. So
 * the weight in these clips is carried by the **neck and the topline** rather
 * than by ear and snout accents, and every one of them is slower than the
 * equivalent on a piglet — she is four times the mass and reads wrong at
 * Rosalie's tempo. That difference is the point of her being in the game.
 *
 * ## The reach is measured, not eyeballed, and the bar moved because of it
 *
 * `hollyReachClip` is the only clip in this project whose numbers came out of a
 * solver. Beat 9's door needs her muzzle above the drop bar **with her nose
 * still forward of her body** — a reach that puts her nose over her own back is
 * not a sheep opening a gate, it is a sheep howling — and that pair of
 * constraints turns out to be tight. Her whole forward-reach envelope:
 *
 *   nose 0.60 m forward of her root  ->  1.196 m
 *   nose 0.50 m forward              ->  1.267 m
 *   nose 0.39 m forward              ->  1.355 m   <- the authored pose
 *
 * The bar was authored at 1.40 m from a `MUZZLE_Y` that was itself wrong by
 * 95 mm (see the note on that constant in `holly.ts`). There is no pose that
 * reaches 1.40 m without her standing inside the doorway she is opening. **The
 * bar came down to 1.25 m**, which she clears by 0.105 m at a 23° rear, and
 * which is still nearly four times Rosalie's standing height — what the beat
 * needs is that ROSALIE cannot reach it, and 1.25 is as far out of a 0.32 m
 * piglet's reach as 1.40 was.
 *
 * `tests/e2e/holly-rig.spec.ts` re-measures this against the bar's own exported
 * height, so if either number moves the failure arrives in metres.
 */

import { ease } from './types';
import { type HollyClip, type HollyLegPose, type HollyPose } from './hollyTypes';

/**
 * The reach pose, at full extension. Solved, then rounded to 0.01 rad.
 *
 * Do not tune these three by eye against a screenshot: they trade off against
 * each other and against how far in front of the bar she stands. Change one and
 * re-run the reach gate, which reports the muzzle height it measured.
 */
export const HOLLY_REACH = { rear: 0.40, neckPitch: 0.20, headPitch: -0.70 } as const;

/**
 * How far forward of her root the reach puts her muzzle, metres.
 *
 * This is what decides where she stands: her latch station is the bar's Z minus
 * this. Exported because the puzzle needs it and a second copy of it would be
 * the same defect `MUZZLE_Y` records.
 */
export const HOLLY_REACH_FORWARD = 0.39;

function setLeg(out: HollyLegPose, swing: number, splay: number, lift: number): void {
  out.swing = swing;
  out.splay = splay;
  out.lift = lift;
}

/** Slow stance, fast swing. Same shape as `clips.ts`, and for the same reason. */
function stancePhase(phase: number, duty: number): number {
  const t = phase % 1;
  if (t < duty) return 1 - (t / duty) * 2;
  return -1 + ease.inOut((t - duty) / (1 - duty)) * 2;
}

function liftPhase(phase: number, duty: number, height: number): number {
  const t = phase % 1;
  if (t < duty) return 0;
  return Math.sin(((t - duty) / (1 - duty)) * Math.PI) * height;
}

/**
 * The breath, written as a function so the trapped clip can run it faster.
 *
 * **Asymmetric on purpose.** A real breath draws in quickly and lets go slowly;
 * a sine wave spends equal time in both and reads as a machine. At her size this
 * is visible — she is 1.24 m of pale flank filling a quarter of the frame — and
 * on an animal with no facial performance it is most of what says she is alive
 * rather than placed.
 */
function breath(phase: number, depth: number): { swell: number; rise: number } {
  const t = phase % 1;
  const IN = 0.38;
  const curve = t < IN ? ease.out(t / IN) : 1 - ease.inOut((t - IN) / (1 - IN));
  return { swell: 1 + curve * depth, rise: curve * depth * 0.5 };
}

function clearPose(out: HollyPose): void {
  out.bob = 0;
  out.breathe = 1;
  out.rear = 0;
  out.pitch = 0;
  out.roll = 0;
  out.neckPitch = 0;
  out.neckYaw = 0;
  out.headPitch = 0;
  out.headYaw = 0;
  setLeg(out.legFL, 0, 0, 0);
  setLeg(out.legFR, 0, 0, 0);
  setLeg(out.legHL, 0, 0, 0);
  setLeg(out.legHR, 0, 0, 0);
}

/**
 * Standing, breathing. The bed every other state blends out of.
 *
 * 4.6 s is a resting ewe's rate and it is deliberately much slower than
 * Rosalie's idle. Two animals breathing at the same speed in one two-shot read
 * as one animation applied twice.
 */
export const hollyBreathClip: HollyClip = {
  id: 'holly-breath',
  duration: 4.6,
  loop: true,
  sample(phase: number, out: HollyPose): void {
    clearPose(out);
    const b = breath(phase, 0.016);
    out.breathe = b.swell;
    out.bob = b.rise;
    // The head drifts a few millimetres with the ribcage. Mechanical, not
    // emotional — she is not sighing, her chest is moving and her neck is on it.
    out.neckPitch = -b.rise * 1.4;
  },
};

/**
 * Caught in the wire: standing, head low, one hind leg pulled out behind her.
 *
 * **She is patient, not thrashing**, and `docs/beat-9-plan.md` is specific about
 * that — the scene's whole argument is that she has been here a while and has
 * stopped fighting it. So this is a held pose with a faster, shallower breath
 * over it, and nothing in it is a struggle animation.
 *
 * The one asymmetry doing the work is the hind left: swung back and splayed out
 * where the panel has it, with her weight rolled off it onto the other three.
 * That reads as *caught* from any angle, which a symmetrical standing pose plus
 * a wire mesh does not — the wire alone looks like scenery she is standing near.
 */
export const hollyTrappedClip: HollyClip = {
  id: 'holly-trapped',
  duration: 3.4,
  loop: true,
  sample(phase: number, out: HollyPose): void {
    clearPose(out);
    const b = breath(phase, 0.011);
    out.breathe = b.swell;
    out.bob = b.rise;

    // Head down and turned toward whoever has just come in. This is the
    // attention channel doing its job (D-002): she is looking at Rosalie,
    // and that is a fact about where the player is, not a mood.
    out.neckPitch = 0.26 - b.rise * 1.4;
    out.neckYaw = -0.30;
    out.headPitch = 0.06;
    out.headYaw = -0.14;

    // Weight off the trapped leg.
    out.roll = 0.055;
    setLeg(out.legHL, -0.44, 0.20, 0);
    setLeg(out.legHR, 0.06, -0.03, 0);
    setLeg(out.legFL, 0.03, 0, 0);
    setLeg(out.legFR, -0.05, 0, 0);
  },
};

/**
 * Following. A four-beat walk, diagonal pairs, and slower than it looks like it
 * should be.
 *
 * 1.05 s a cycle at a 1.15 m/s follow speed is a long stride, which is correct
 * for a leggy animal and wrong for a compact one — set it at Rosalie's tempo and
 * she scurries, which is the single fastest way to lose the size difference the
 * two-shot exists for.
 */
export const hollyWalkClip: HollyClip = {
  id: 'holly-walk',
  duration: 1.05,
  loop: true,
  sample(phase: number, out: HollyPose): void {
    clearPose(out);
    const DUTY = 0.62;
    const SWING = 0.30;
    const LIFT = 0.045;

    // Diagonal pairs: front-left with hind-right, front-right with hind-left.
    setLeg(out.legFL, stancePhase(phase, DUTY) * SWING, 0, liftPhase(phase, DUTY, LIFT));
    setLeg(out.legHR, stancePhase(phase, DUTY) * SWING * 0.86, 0, liftPhase(phase, DUTY, LIFT * 0.8));
    setLeg(out.legFR, stancePhase(phase + 0.5, DUTY) * SWING, 0, liftPhase(phase + 0.5, DUTY, LIFT));
    setLeg(out.legHL, stancePhase(phase + 0.5, DUTY) * SWING * 0.86, 0, liftPhase(phase + 0.5, DUTY, LIFT * 0.8));

    // Body at twice the stride frequency: it drops as each diagonal pair loads.
    out.bob = ease.cycleCos(phase * 2) * 0.016;
    out.pitch = ease.cycle(phase * 2) * 0.030;
    out.roll = ease.cycle(phase) * 0.045;
    // Her head is on a long neck and it nods once per stride, a beat behind the
    // body. This is the mechanical bob, not a nod at anything.
    out.neckPitch = ease.cycle(phase - 0.12) * 0.055;
    out.breathe = 1 + Math.abs(ease.cycle(phase)) * 0.006;
  },
};

/**
 * The reach: she gets her forefeet up and noses the drop bar out of its brackets.
 *
 * **A one-shot, and its phase is the puzzle's `latchProgress`** — the state
 * machine already produces a 0..1 for exactly this interval, and driving the
 * clip from a second clock would let the picture and the puzzle disagree about
 * when the bar goes. They cannot, because there is only one number.
 *
 * The shape, in phase:
 *
 *   0.00 – 0.18   she gathers: weight back, head comes level
 *   0.18 – 0.68   the rise, eased in and out — this is the whole image
 *   0.68 – 0.86   the flick: the nose goes UP the last few degrees under the bar
 *   0.86 – 1.00   held, front feet on the door
 *
 * The flick is separated from the rise because the bar leaves its brackets on a
 * short upward jerk, not on the way up. Blend them and she appears to lift the
 * bar with her forehead on the way past.
 */
export const hollyReachClip: HollyClip = {
  id: 'holly-reach',
  duration: 1.9,
  loop: false,
  sample(phase: number, out: HollyPose): void {
    clearPose(out);
    const t = Math.min(1, Math.max(0, phase));

    const gather = t < 0.18 ? ease.inOut(t / 0.18) : 1;
    const rise = t < 0.18 ? 0 : ease.inOut(Math.min(1, (t - 0.18) / 0.50));
    const flick = t < 0.68 ? 0 : Math.sin(Math.min(1, (t - 0.68) / 0.18) * Math.PI * 0.5);

    out.rear = rise * HOLLY_REACH.rear;
    // She brings her head LEVEL as she gathers and then bends her neck DOWN as
    // she comes up, which is the opposite of what it looks like it should be and
    // is the single thing that makes the pose work. The rear carries her muzzle
    // up and BACK; bending the neck down is what puts it forward again, under
    // the bar instead of over her own shoulders. The solver found this before
    // any frame did.
    out.neckPitch = gather * -0.08 + rise * (HOLLY_REACH.neckPitch + 0.08);
    out.headPitch = rise * HOLLY_REACH.headPitch - flick * 0.08;

    // Forefeet come off the floor and forward onto the door; hind feet take the
    // weight and spread. `lift` is in metres and her legs are 0.58 m, so these
    // are large numbers next to Rosalie's 16 mm and still modest on her.
    setLeg(out.legFL, rise * 0.72, rise * 0.10, rise * 0.30);
    setLeg(out.legFR, rise * 0.66, rise * -0.10, rise * 0.28);
    // **The hind legs do not SWING, and that is not a stylistic choice.** They
    // are the ones on the ground: a hip rotation of 0.16 rad moves a hoof 0.6 m
    // below it by nearly 100 mm, so a rear that also swings them slides her back
    // feet across the floor in the one shot where the audience is looking at
    // them. They spread for the weight and nothing else — `splay` is about Z and
    // costs 2 mm of lift, which the gate tolerates and the eye does not see.
    setLeg(out.legHL, 0, rise * 0.09, 0);
    setLeg(out.legHR, 0, rise * -0.09, 0);

    // She is working. Faster and deeper than any other clip she has.
    const b = breath(t * 2.4, 0.019);
    out.breathe = b.swell;
    out.bob = b.rise * 0.4;
  },
};

/**
 * Standing asleep, at the end of the beat.
 *
 * **The last thing this beat asks the player to look at is a sheep doing
 * nothing**, and `docs/beat-9-plan.md` is explicit that the absence is the
 * content. That puts the whole weight of the rest scene on this clip: if she
 * reads as a placed prop, the room reads as empty rather than as safe.
 *
 * Three things separate it from `hollyBreathClip`, and none of them is a pose:
 *
 *  - **It is slower than her waking breath, not faster.** 7.2 s against 4.6 s.
 *    Asleep is the one state where the tempo difference is the whole tell, and
 *    it is the only channel available on an animal with no eyelids to close.
 *  - **She sways.** A standing animal asleep is balancing without supervision,
 *    and the sway is the evidence — a perfectly still standing sheep is a
 *    taxidermy sheep. It runs on the breath's own phase rather than on a second
 *    clock, so the roll and the ribcage cannot drift apart into two animals.
 *  - **One hind leg is cocked.** Sheep rest a hind foot, and the resulting
 *    asymmetry in the topline is what says *settled* from behind and from the
 *    side, which are the two angles the rest scene actually offers.
 *
 * The head is DOWN rather than tucked. She has no neck to fold it back along —
 * `holly.ts` gives her one link and a head, not a spine — so the sleeping pose
 * available to this rig is the grazing one held still, and pushing the neck
 * further to fake a tuck only puts her muzzle through her own knee.
 */
export const hollyDozeClip: HollyClip = {
  id: 'holly-doze',
  duration: 7.2,
  loop: true,
  sample(phase: number, out: HollyPose): void {
    clearPose(out);
    // Deeper than standing breath and much slower. A resting animal moves more
    // air per breath, not less, and at her size the flank is where it shows.
    const b = breath(phase, 0.021);
    out.breathe = b.swell;
    // Less bob than the swell would imply: her legs are locked and the ribcage
    // expands against them rather than lifting her.
    out.bob = b.rise * 0.7;

    // Head low and slightly off-axis. `neckPitch` carries it; `headPitch` only
    // adds the last few degrees, because the head is a short link and asking it
    // for the whole angle folds the muzzle into the throat.
    out.neckPitch = 0.36 - b.rise * 1.2;
    out.neckYaw = 0.06;
    out.headPitch = 0.12;
    out.headYaw = -0.05;

    // The sway, on the breath's phase. 10 mrad is about 6 mm at her withers —
    // below the threshold at which it reads as an animation and above the one
    // at which it reads as alive, which is the whole width of the target.
    out.roll = 0.030 + ease.cycle(phase) * 0.010;
    out.pitch = -0.010;

    // The cocked hind leg, and the three that are carrying her.
    setLeg(out.legHR, 0.10, -0.05, 0.006);
    setLeg(out.legHL, -0.04, 0.03, 0);
    setLeg(out.legFL, 0.02, 0.02, 0);
    setLeg(out.legFR, -0.02, -0.02, 0);
  },
};

export const HOLLY_CLIPS = {
  breath: hollyBreathClip,
  trapped: hollyTrappedClip,
  walk: hollyWalkClip,
  reach: hollyReachClip,
  doze: hollyDozeClip,
} as const;
